import { useMemo } from "react";
import { ConnectProto } from "@racoon/api";
import { useListUsersQuery } from "~/shared/api/user/user.grpc";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { DateValue } from "~/shared/lib/analytics-utils";
import { personName, shiftDate } from "./job-report";
import { ReportDriverOnShift, useReportDriverOnShifts } from "./use-report-data";

/** Drivers run a shift; loaders ride on it as crew. */
export type StaffKind = "driver" | "loader";

/** One row of «Реестр водителей» / «Реестр грузчиков» (AIS 15.3.2 / 15.3.3). */
export interface StaffRegistryRow {
  userId: bigint;
  name: string;
  /** Truck of the employee's latest shift in the period; null without one. */
  truckId: bigint | null;
  truckPlateNumber: string;
}

const ROLE: Record<StaffKind, ConnectProto.UserRole> = {
  driver: ConnectProto.UserRole.DRIVER,
  loader: ConnectProto.UserRole.PROVIDER_LOADER,
};

const shiftMembers = (shift: ReportDriverOnShift, kind: StaffKind): bigint[] =>
  kind === "driver" ? (shift.driverId ? [shift.driverId] : []) : shift.crewIds;

/** Active drivers or loaders with the truck of their latest shift in the period. */
export function useStaffRegistryRows(kind: StaffKind, startDate: DateValue, endDate: DateValue) {
  const { activeCompanyId } = useAuthSession();
  const users = useListUsersQuery(
    {
      ownerOrganizationIds: [BigInt(activeCompanyId ?? 0)],
      roles: [ROLE[kind]],
      statuses: [ConnectProto.UserStatus.ACTIVE],
    },
    { skip: !activeCompanyId },
  );
  const shifts = useReportDriverOnShifts(startDate, endDate);

  const latestShiftByUser = useMemo(() => {
    const latest = new Map<string, { shift: ReportDriverOnShift; time: number }>();
    for (const shift of shifts.rows) {
      const time = shiftDate(shift)?.getTime() ?? 0;
      for (const userId of shiftMembers(shift, kind)) {
        const current = latest.get(String(userId));
        if (!current || time > current.time) latest.set(String(userId), { shift, time });
      }
    }
    return latest;
  }, [shifts.rows, kind]);

  const rows = useMemo<StaffRegistryRow[]>(
    () =>
      (users.data?.users ?? [])
        .map((user) => {
          const shift = latestShiftByUser.get(String(user.id))?.shift;
          return {
            userId: user.id,
            name: personName(user),
            truckId: shift?.truckId || null,
            truckPlateNumber: shift?.truckPlateNumber ?? "",
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name)),
    [users.data, latestShiftByUser],
  );

  return {
    rows,
    isLoading: users.isLoading || users.isFetching || shifts.isLoading,
    error: users.error ?? shifts.error,
  };
}
